/**
 * Situação de todos os cursos (admin). Lista com pesquisa por nº (ID_Curso),
 * ID_Produto, nome, datas de início/fim e estado (Agendado/Draft/Encerrado),
 * e acesso ao detalhe em /admin/cursos/edit?curso=ID_Curso.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Search, X, ArrowRight, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { adminApi } from "../api";

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("pt-PT"); } catch { return v; }
};

const ESTADO_STYLE = {
  A: "bg-emerald-50 text-emerald-700 border-emerald-200",
  D: "bg-amber-50 text-amber-700 border-amber-200",
  E: "bg-gray-100 text-gray-600 border-gray-200",
  C: "bg-red-50 text-red-700 border-red-200",
  X: "bg-slate-50 text-slate-500 border-slate-200",
};

const EMPTY = { q: "", id_produto: "", estado: "", dtp: "", tipo: "", inicio_de: "", inicio_ate: "", fim_de: "", fim_ate: "" };

export default function AdminCursosSituacao() {
  const [filters, setFilters] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sort, setSort] = useState({ key: "DataInicio", dir: "desc" });

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    const num = new Set(["ID_Curso", "ID_Produto"]);
    const dates = new Set(["DataInicio", "DataFim"]);
    arr.sort((a, b) => {
      let va = a[key], vb = b[key];
      if (num.has(key)) { va = Number(va) || 0; vb = Number(vb) || 0; return (va - vb) * mul; }
      if (dates.has(key)) { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; return (va - vb) * mul; }
      va = (va ?? "").toString().toLowerCase();
      vb = (vb ?? "").toString().toLowerCase();
      return va.localeCompare(vb, "pt") * mul;
    });
    return arr;
  }, [rows, sort]);

  const load = useCallback(async (f) => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v && String(v).trim()) params.append(k, v); });
      const r = await adminApi.cursosSituacao(params.toString());
      setRows(r.data || []);
      if (r.estados) setEstados(r.estados);
    } catch (e) { setErr(e.message || "Erro ao carregar cursos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(EMPTY); }, [load]);

  const setField = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  const onSearch = (e) => { e?.preventDefault?.(); load(filters); };
  const onClear = () => { setFilters(EMPTY); load(EMPTY); };

  const inputCls = "w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829]";
  const labelCls = "block text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold mb-1";

  return (
    <div data-testid="admin-cursos-situacao" className="max-w-6xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><GraduationCap size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Situação dos Cursos</h1>
          <p className="text-sm text-[#4B5563]">Todos os cursos (agendados, encerrados e draft), com pesquisa e acesso ao detalhe.</p>
        </div>
      </div>

      {/* Filtros */}
      <form onSubmit={onSearch} className="p-4 bg-white border border-[#E6E1D6] rounded-md mb-5" data-testid="cursos-situacao-filtros">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div className="md:col-span-2">
            <label className={labelCls}>Nº, Nome ou ID Produto</label>
            <input value={filters.q} onChange={(e) => setField("q", e.target.value)} placeholder="Ex.: 2251, PNL, Coaching..."
              className={inputCls} data-testid="filtro-q" />
          </div>
          <div>
            <label className={labelCls}>ID Produto</label>
            <input value={filters.id_produto} onChange={(e) => setField("id_produto", e.target.value)} placeholder="Ex.: 1234"
              className={inputCls} data-testid="filtro-id-produto" />
          </div>
          <div>
            <label className={labelCls}>Estado do Curso</label>
            <select value={filters.estado} onChange={(e) => setField("estado", e.target.value)} className={inputCls} data-testid="filtro-estado">
              <option value="">Todos</option>
              {estados.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Tipo de Curso</label>
            <select value={filters.tipo} onChange={(e) => setField("tipo", e.target.value)} className={inputCls} data-testid="filtro-tipo">
              <option value="">Todos</option>
              <option value="publico">Público em geral</option>
              <option value="incompany">In-company (empresa)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>DTP</label>
            <select value={filters.dtp} onChange={(e) => setField("dtp", e.target.value)} className={inputCls} data-testid="filtro-dtp">
              <option value="">Todos</option>
              <option value="gerado">Gerado</option>
              <option value="pendente">Pendente</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Data início (de / até)</label>
            <div className="flex gap-2">
              <input type="date" value={filters.inicio_de} onChange={(e) => setField("inicio_de", e.target.value)} className={inputCls} data-testid="filtro-inicio-de" />
              <input type="date" value={filters.inicio_ate} onChange={(e) => setField("inicio_ate", e.target.value)} className={inputCls} data-testid="filtro-inicio-ate" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Data fim (de / até)</label>
            <div className="flex gap-2">
              <input type="date" value={filters.fim_de} onChange={(e) => setField("fim_de", e.target.value)} className={inputCls} data-testid="filtro-fim-de" />
              <input type="date" value={filters.fim_ate} onChange={(e) => setField("fim_ate", e.target.value)} className={inputCls} data-testid="filtro-fim-ate" />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" data-testid="cursos-situacao-pesquisar"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#0F1829] text-white rounded hover:bg-[#1c2a45]">
            <Search size={15} /> Pesquisar
          </button>
          <button type="button" onClick={onClear} data-testid="cursos-situacao-limpar"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB]">
            <X size={15} /> Limpar
          </button>
        </div>
      </form>

      {/* Resultados */}
      <div className="bg-white border border-[#E6E1D6] rounded-md overflow-hidden">
        <div className="px-4 py-2 border-b border-[#F3F0E7] flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-[0.15em] text-[#978D78] font-bold">
            {loading ? "A carregar..." : `${rows.length} curso(s)`}
          </span>
        </div>
        {err && <p className="p-4 text-sm text-red-600">{err}</p>}
        {loading ? (
          <div className="p-8 text-center text-[#6B7280]"><Loader2 size={20} className="animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-[#6B7280] text-sm">Nenhum curso encontrado com estes filtros.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cursos-situacao-tabela">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[#978D78] border-b border-[#F3F0E7]">
                  <Th k="ID_Curso" sort={sort} onSort={toggleSort}>Nº</Th>
                  <Th k="NomeCurso" sort={sort} onSort={toggleSort}>Curso</Th>
                  <Th k="ID_Produto" sort={sort} onSort={toggleSort}>Produto</Th>
                  <Th k="DataInicio" sort={sort} onSort={toggleSort}>Início</Th>
                  <Th k="DataFim" sort={sort} onSort={toggleSort}>Fim</Th>
                  <Th k="InCompany" sort={sort} onSort={toggleSort}>Tipo</Th>
                  <Th k="EstadoCurso" sort={sort} onSort={toggleSort}>Estado</Th>
                  <Th k="DtpEstado" sort={sort} onSort={toggleSort}>DTP</Th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((c) => (
                  <tr key={c.ID_Curso} className="border-b border-[#F7F5EF] hover:bg-[#FAF8F2]" data-testid={`curso-row-${c.ID_Curso}`}>
                    <td className="px-4 py-2 font-mono text-[#0F1829]">{c.ID_Curso}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-[#0F1829]">{c.NomeCurso || c.Descricao || `Produto ${c.ID_Produto}`}</div>
                      {c.Local && <div className="text-xs text-[#978D78]">{c.Local}</div>}
                    </td>
                    <td className="px-4 py-2 font-mono text-[#6B7280]">{c.ID_Produto ?? "—"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDate(c.DataInicio)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDate(c.DataFim)}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${c.InCompany === "S" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                        {c.InCompany === "S" ? "In-company" : "Público"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${ESTADO_STYLE[c.ID_EstadoCurso] || "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        {c.EstadoCurso || c.ID_EstadoCurso || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${c.DtpEstado === "Pendente" ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                        {c.DtpEstado || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link to={`/admin/cursos/edit?curso=${c.ID_Curso}`} data-testid={`curso-detalhe-${c.ID_Curso}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-[#E6E1D6] hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition">
                        Detalhe <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function Th({ k, sort, onSort, children }) {
  const active = sort.key === k;
  return (
    <th className="px-4 py-2">
      <button
        type="button"
        onClick={() => onSort(k)}
        data-testid={`sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-[0.12em] hover:text-[#0F1829] ${active ? "text-[#0F1829]" : ""}`}
      >
        {children}
        {active && (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </button>
    </th>
  );
}
